import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dialog } from 'primereact/dialog';
import { FormattedMessage, useIntl as useIntlGlobal } from 'react-intl';

import FinishTrainingDialog from '../components/dialogs/FinishTrainingDialog';
import VideoDialog from '../components/dialogs/VideoDialog';
import RpeDropdownComponent, { getRpeOptionsForMethod } from '../components/RpeDropdown';
import RpeHeatStrip from '../components/shared/RpeHeatStrip';
import { getYouTubeThumbnail } from '../utils/UtilFunctions';
import { api } from '../services/api-client';
import { IRpeMethod } from 'types/rpe/rpe-method-assigned';

import {
  useTrainingPlanDetails,
  ITrainingPlanDetailsProps,
  IExerciseProgressEntry
} from '../hooks/useTrainingPlanDetails';
import { IExerciseGroup } from 'types/workout/exercise-group';
import { IExerciseInstance } from 'types/workout/exercise-instance';
import { IExerciseSetConfiguration } from 'types/workout/exercise-set-configuration';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type MeasurableField = 'repetitions' | 'weight' | 'time' | 'distance' | 'duration' | 'difficulty' | 'tempo';

/** Returns true if the field should be shown for this exercise + set index */
function hasField(exercise: IExerciseInstance, setIndex: number, field: MeasurableField): boolean {
  if (exercise[field]) return true;
  const config = (exercise.setConfiguration || []).find((c: IExerciseSetConfiguration) => c.setNumber === setIndex + 1);
  return !!(config && config[field]);
}

/** Gets the template value for this set (from setConfiguration or exercise-level) */
function getSetTemplateValue(exercise: IExerciseInstance, setIndex: number, field: MeasurableField): string {
  const config = (exercise.setConfiguration || []).find((c: IExerciseSetConfiguration) => c.setNumber === setIndex + 1);
  return config?.[field] || exercise[field] || '';
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = {
  page: {
    padding: '0.75rem',
    maxWidth: '720px',
    margin: '0 auto',
    paddingBottom: '5rem'
  } as React.CSSProperties,
  headerCard: {
    background: 'var(--ios-card-bg)',
    borderRadius: 'var(--ios-radius-xl)',
    border: '1px solid var(--ios-card-border)',
    boxShadow: 'var(--ios-card-shadow)',
    padding: '1.25rem',
    marginBottom: '1rem',
    textAlign: 'center' as const
  } as React.CSSProperties,
  planTitle: {
    fontSize: 'clamp(1.1rem, 3vw, 1.5rem)',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    margin: '0 0 0.15rem',
    color: 'var(--ios-text)'
  } as React.CSSProperties,
  planSubtitle: {
    fontSize: 'clamp(0.82rem, 2vw, 0.95rem)',
    color: 'var(--ios-text-secondary)',
    fontWeight: 500,
    margin: 0
  } as React.CSSProperties,
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    fontSize: '0.78rem',
    color: 'var(--ios-text-tertiary)',
    marginTop: '0.5rem'
  } as React.CSSProperties,
  exerciseCard: (completed: boolean) =>
    ({
      background: 'var(--ios-card-bg)',
      borderRadius: 'var(--ios-radius-lg)',
      border: completed ? '1px solid rgba(34,197,94,0.25)' : '1px solid var(--ios-card-border)',
      boxShadow: completed ? '0 0 0 1px rgba(34,197,94,0.08), var(--ios-shadow-sm)' : 'var(--ios-card-shadow)',
      padding: '0.85rem',
      transition: 'all 0.25s ease'
    }) as React.CSSProperties,
  exerciseName: {
    fontSize: 'clamp(0.88rem, 2.5vw, 1rem)',
    fontWeight: 600,
    margin: 0,
    letterSpacing: '-0.01em',
    color: 'var(--ios-text)'
  } as React.CSSProperties,
  videoThumb: {
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    width: '3.5rem',
    height: '2.5rem',
    cursor: 'pointer',
    flexShrink: 0
  } as React.CSSProperties,
  setRow: (completed: boolean | null | undefined) =>
    ({
      padding: '0.5rem 0.65rem',
      background:
        completed === true
          ? 'rgba(34, 197, 94, 0.04)'
          : completed === false
            ? 'rgba(239, 68, 68, 0.03)'
            : 'var(--ios-surface-subtle)',
      borderRadius: 'var(--ios-radius-md)',
      border:
        completed === true
          ? '1px solid rgba(34,197,94,0.12)'
          : completed === false
            ? '1px solid rgba(239,68,68,0.08)'
            : '1px solid transparent',
      transition: 'all 0.2s ease'
    }) as React.CSSProperties,
  inputLabel: {
    display: 'block',
    fontSize: '0.72rem',
    fontWeight: 600,
    marginBottom: '0.2rem',
    color: 'var(--ios-text-secondary)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em'
  } as React.CSSProperties,
  stickyBar: {
    position: 'fixed' as const,
    bottom: '0.75rem',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    background: 'var(--ios-glass-bg)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    borderRadius: 'var(--ios-radius-xl)',
    boxShadow: 'var(--ios-shadow-xl)',
    border: '1px solid var(--ios-glass-border)',
    maxWidth: '95vw',
    width: 'auto'
  } as React.CSSProperties,
  timerPill: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    background: '#a3e635',
    color: '#0a0a0a',
    padding: '0.35rem 0.6rem',
    borderRadius: 'var(--ios-radius-md)'
  } as React.CSSProperties,
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.75rem'
  } as React.CSSProperties,
  groupTitle: {
    fontSize: 'clamp(0.95rem, 2.5vw, 1.1rem)',
    fontWeight: 700,
    color: '#4d7c0f',
    margin: 0
  } as React.CSSProperties,
  groupBadge: {
    background: 'rgba(163, 230, 53, 0.18)',
    color: '#4d7c0f',
    padding: '0.15rem 0.5rem',
    borderRadius: 'var(--ios-radius-pill)',
    fontSize: '0.75rem',
    fontWeight: 600
  } as React.CSSProperties,
  navButton: (disabled: boolean) =>
    ({
      width: '2.2rem',
      height: '2.2rem',
      color: disabled ? 'var(--ios-text-tertiary)' : '#4d7c0f',
      flexShrink: 0
    }) as React.CSSProperties,
  groupDot: (active: boolean, completed: boolean) =>
    ({
      width: active ? '2rem' : '1.5rem',
      height: '0.3rem',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      background: completed ? '#22c55e' : active ? '#0a0a0a' : 'var(--ios-surface-muted)'
    }) as React.CSSProperties,
  actionBtn: (bg: string, border?: string) =>
    ({
      width: '2.2rem',
      height: '2.2rem',
      background: border ? 'transparent' : bg,
      border: border ? `1.5px solid ${border}` : 'none',
      color: border || '#fff'
    }) as React.CSSProperties
};

// ---------------------------------------------------------------------------
// Render helpers
// ---------------------------------------------------------------------------

/**
 * v2 quick-complete RPE picker (design handoff §4.6).
 *
 * When the resolved `rpeMethod` has discrete `valuesMeta`, render the
 * heat-strip — tapping a chip auto-commits the set (`completed = true` +
 * `rating = value`). For continuous-step custom methods that don't fit chips,
 * we fall back to the legacy InputNumber-based picker.
 */
function QuickCompleteRpe({
  rpeMethod,
  selectedRaw,
  onSelect,
  onSkip,
  fallbackPicker
}: {
  rpeMethod: IRpeMethod;
  selectedRaw: number | string | null;
  onSelect: (value: number) => void;
  onSkip: () => void;
  fallbackPicker: (onChange: (e: { value: number | null }) => void) => JSX.Element;
}): JSX.Element {
  const intl = useIntlGlobal();
  const options = useMemo(() => getRpeOptionsForMethod(rpeMethod), [rpeMethod]);
  // Use heat-strip only when the method has a manageable chip count (≤ 12)
  const usesHeatStrip = options.length > 0 && options.length <= 12;
  if (!usesHeatStrip) {
    return (
      <div className="flex flex-column gap-2">
        {fallbackPicker((e) => {
          if (e.value !== null && e.value !== undefined) onSelect(e.value);
        })}
        <button type="button" onClick={onSkip} className="et-quickcomplete__skip" style={{ alignSelf: 'flex-start' }}>
          {intl.formatMessage({ id: 'session.skip' })}
        </button>
      </div>
    );
  }
  const selectedLabel = selectedRaw !== null && selectedRaw !== undefined ? String(selectedRaw) : null;
  return (
    <div className="flex flex-column gap-2">
      <RpeHeatStrip
        values={options.map((o) => ({
          label: o.label,
          numeric: o.value,
          colorOverride: o.color
        }))}
        selected={selectedLabel}
        onSelect={(label) => {
          const numeric = Number(label);
          if (Number.isFinite(numeric)) onSelect(numeric);
        }}
      />
      <div className="flex justify-content-between align-items-center">
        <span style={{ fontSize: '11px', color: 'var(--ios-text-tertiary)' }}>
          {intl.formatMessage({ id: 'session.quickComplete.hint' })}
        </span>
        <button type="button" onClick={onSkip} className="et-quickcomplete__skip">
          {intl.formatMessage({ id: 'session.skip' })}
        </button>
      </div>
    </div>
  );
}

// Last-completed-set data per exercise. Populated once by the page-level
// effect that calls /workout/exercise-history/last-batch — see qa-audit
// 2026-05-21 Bug 10. `null` means: loaded and confirmed empty. `undefined`
// means: not loaded yet (chip stays hidden during initial paint).
export type LastSetData = {
  repetitions: string | null;
  weight: string | null;
  rpe: string | null;
  sessionDate: string | null;
} | null;

export type LastSetsMap = Record<number, LastSetData>;

export const LastSetsContext = createContext<LastSetsMap>({});

/**
 * Shows "Última: 8×60kg @ RPE 7" for a given exercise. Reads the data from
 * {@link LastSetsContext}, which the page populates once with a single
 * batch call. Renders nothing while the map is empty / loading or when the
 * client has no history for the exercise.
 */
export function LastSetChip({ exerciseId }: { exerciseId: number }): JSX.Element | null {
  const intl = useIntlGlobal();
  const map = useContext(LastSetsContext);
  const data = map[exerciseId];

  if (!data || (!data.repetitions && !data.weight)) return null;

  const reps = data.repetitions ?? '?';
  const weight = data.weight ?? '?';
  const rpe = data.rpe ?? '?';
  return (
    <div className="et-quickcomplete__last">
      <i className="pi pi-history" aria-hidden style={{ fontSize: '0.7rem' }} />
      <span>{intl.formatMessage({ id: 'session.compare.last' }, { reps, weight, rpe })}</span>
    </div>
  );
}

function RenderExerciseGroup({
  group,
  exerciseProgress,
  isGroupCompleted,
  isExerciseCompleted,
  handleToggleAll,
  handleVideoClick,
  handleSetCompletedChange,
  handleExerciseChange,
  propertyUnits,
  getExerciseRpeMethod,
  intl
}: {
  group: IExerciseGroup;
  exerciseProgress: ReturnType<typeof useTrainingPlanDetails>['exerciseProgress'];
  isGroupCompleted: ReturnType<typeof useTrainingPlanDetails>['isGroupCompleted'];
  isExerciseCompleted: ReturnType<typeof useTrainingPlanDetails>['isExerciseCompleted'];
  handleToggleAll: ReturnType<typeof useTrainingPlanDetails>['handleToggleAll'];
  handleVideoClick: ReturnType<typeof useTrainingPlanDetails>['handleVideoClick'];
  handleSetCompletedChange: ReturnType<typeof useTrainingPlanDetails>['handleSetCompletedChange'];
  handleExerciseChange: ReturnType<typeof useTrainingPlanDetails>['handleExerciseChange'];
  propertyUnits: ReturnType<typeof useTrainingPlanDetails>['propertyUnits'];
  getExerciseRpeMethod: ReturnType<typeof useTrainingPlanDetails>['getExerciseRpeMethod'];
  intl: ReturnType<typeof useTrainingPlanDetails>['intl'];
}): JSX.Element {
  const completed = isGroupCompleted(group);
  const completedCount = group.exercises.filter((ex: IExerciseInstance) => exerciseProgress[ex.id]?.completed).length;

  return (
    <div style={{ opacity: completed ? 0.7 : 1, transition: 'opacity 0.3s ease' }}>
      {/* Group Header */}
      <div style={styles.groupHeader}>
        <h3 style={styles.groupTitle}>
          <FormattedMessage id="training.group" values={{ number: group.groupNumber }} />
        </h3>
        <span style={styles.groupBadge}>
          {completedCount}/{group.exercises.length}
        </span>
      </div>

      {/* Exercise List */}
      <div className="flex flex-column gap-3">
        {group.exercises.map((exercise: IExerciseInstance) => {
          const progress: IExerciseProgressEntry = exerciseProgress[exercise.id] || {
            sets: [],
            completed: null,
            comments: ''
          };
          const exerciseCompleted = isExerciseCompleted(exercise);

          return (
            <div key={exercise.id} style={styles.exerciseCard(exerciseCompleted)}>
              {/* Exercise Header */}
              <div className="flex align-items-center justify-content-between gap-2 mb-2">
                <div className="flex align-items-center gap-2 flex-grow-1 min-w-0">
                  <div style={styles.videoThumb} onClick={() => handleVideoClick(exercise.exercise.multimedia)}>
                    <img
                      className="w-full h-full"
                      style={{ objectFit: 'cover' }}
                      src={getYouTubeThumbnail(exercise.exercise.multimedia)}
                      alt="Video"
                    />
                  </div>
                  <h3 style={styles.exerciseName}>{exercise.exercise.name}</h3>
                </div>

                <div className="flex gap-1 flex-shrink-0">
                  <Button
                    icon="pi pi-check"
                    size="small"
                    className="p-button-rounded p-button-text"
                    onClick={() => handleToggleAll(true, exercise.id)}
                    tooltip={intl.formatMessage({
                      id: 'training.markAllCompleted',
                      defaultMessage: 'Mark All Completed'
                    })}
                    tooltipOptions={{ position: 'top' }}
                    style={{ width: '1.65rem', height: '1.65rem', color: '#22c55e' }}
                  />
                  <Button
                    icon="pi pi-times"
                    size="small"
                    className="p-button-rounded p-button-text"
                    onClick={() => handleToggleAll(false, exercise.id)}
                    tooltip={intl.formatMessage({ id: 'training.markAllSkipped', defaultMessage: 'Mark All Skipped' })}
                    tooltipOptions={{ position: 'top' }}
                    style={{ width: '1.65rem', height: '1.65rem', color: '#ef4444' }}
                  />
                </div>
              </div>

              {/* v2 comparison chip: last completed set of this exercise. */}
              <LastSetChip exerciseId={exercise.exercise.id} />

              {/* Sets */}
              <div className="flex flex-column gap-2">
                {Array.from({ length: parseInt(exercise.sets as string) || group.set || 1 }).map((_, index) => {
                  const setData = progress.sets?.[index] || {};
                  return (
                    <div key={index} style={styles.setRow(setData.completed)}>
                      {/* Set header with radio buttons */}
                      <div className="flex align-items-center justify-content-between mb-2">
                        <span style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--ios-text)' }}>
                          Set {index + 1}
                        </span>
                        <div className="flex align-items-center gap-2">
                          <div className="flex align-items-center gap-1">
                            <RadioButton
                              inputId={`completed-yes-${exercise.id}-${index + 1}`}
                              name={`completed-${exercise.id}-${index + 1}`}
                              value={true}
                              onChange={(e) => handleSetCompletedChange(exercise.id, index, e.value)}
                              checked={setData.completed === true}
                            />
                            <label
                              htmlFor={`completed-yes-${exercise.id}-${index + 1}`}
                              style={{ fontSize: '0.8rem', color: '#22c55e', fontWeight: 700, cursor: 'pointer' }}
                            >
                              &#10003;
                            </label>
                          </div>
                          <div className="flex align-items-center gap-1">
                            <RadioButton
                              inputId={`completed-no-${exercise.id}-${index + 1}`}
                              name={`completed-${exercise.id}-${index + 1}`}
                              value={false}
                              onChange={(e) => handleSetCompletedChange(exercise.id, index, e.value)}
                              checked={setData.completed === false}
                            />
                            <label
                              htmlFor={`completed-no-${exercise.id}-${index + 1}`}
                              style={{ fontSize: '0.8rem', color: '#ef4444', fontWeight: 700, cursor: 'pointer' }}
                            >
                              &#10007;
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Set template info (when setConfiguration has per-set values) */}
                      {exercise.setConfiguration && exercise.setConfiguration.length > 0 && (
                        <div
                          className="flex flex-wrap gap-2 mb-2"
                          style={{ fontSize: '0.72rem', color: 'var(--ios-text-tertiary)' }}
                        >
                          {(() => {
                            const tmpl = getSetTemplateValue;
                            const fields: { key: MeasurableField; unit: string }[] = [
                              { key: 'repetitions', unit: propertyUnits.repetitions || 'reps' },
                              { key: 'weight', unit: propertyUnits?.weight || 'kg' },
                              { key: 'time', unit: propertyUnits?.time || 's' },
                              { key: 'distance', unit: propertyUnits?.distance || 'km' },
                              { key: 'duration', unit: propertyUnits?.duration || 's' },
                              { key: 'difficulty', unit: propertyUnits?.difficulty || '' },
                              { key: 'tempo', unit: propertyUnits?.tempo || '' }
                            ];
                            return fields
                              .filter((f) => tmpl(exercise, index, f.key))
                              .map((f) => (
                                <span
                                  key={f.key}
                                  style={{
                                    background: 'rgba(163, 230, 53, 0.18)',
                                    padding: '0.1rem 0.4rem',
                                    borderRadius: 'var(--ios-radius-sm)',
                                    fontWeight: 600
                                  }}
                                >
                                  {tmpl(exercise, index, f.key)}
                                  {f.unit ? ` ${f.unit}` : ''}
                                </span>
                              ));
                          })()}
                        </div>
                      )}

                      {/* Input fields grid */}
                      <div className="grid">
                        {hasField(exercise, index, 'repetitions') && (
                          <div className="col-4 sm:col-4 md:col-3">
                            <label style={styles.inputLabel}>
                              {intl.formatMessage({ id: 'training.exercise.reps' }) +
                                (propertyUnits.repetitions ? ` (${propertyUnits.repetitions})` : '')}
                            </label>
                            <InputText
                              value={setData.repetitions || ''}
                              onChange={(e) => handleExerciseChange(exercise.id, index, 'repetitions', e.target.value)}
                              className="p-inputtext-sm w-full text-center"
                              placeholder={getSetTemplateValue(exercise, index, 'repetitions') || '0'}
                              style={{ borderRadius: 'var(--ios-radius-sm)' }}
                            />
                          </div>
                        )}
                        {hasField(exercise, index, 'weight') && (
                          <div className="col-4 sm:col-4 md:col-3">
                            <label style={styles.inputLabel}>
                              {intl.formatMessage(
                                { id: 'training.exercise.weight' },
                                { unit: propertyUnits?.weight || 'kg' }
                              )}
                            </label>
                            <InputText
                              value={setData.weight || ''}
                              onChange={(e) => handleExerciseChange(exercise.id, index, 'weight', e.target.value)}
                              className="p-inputtext-sm w-full text-center"
                              placeholder={getSetTemplateValue(exercise, index, 'weight') || '0'}
                              style={{ borderRadius: 'var(--ios-radius-sm)' }}
                            />
                          </div>
                        )}
                        {hasField(exercise, index, 'time') && (
                          <div className="col-4 sm:col-4 md:col-3">
                            <label style={styles.inputLabel}>
                              {intl.formatMessage(
                                { id: 'training.exercise.time' },
                                { unit: propertyUnits?.time || 's' }
                              )}
                            </label>
                            <InputText
                              value={setData.time || ''}
                              onChange={(e) => handleExerciseChange(exercise.id, index, 'time', e.target.value)}
                              className="p-inputtext-sm w-full text-center"
                              placeholder={getSetTemplateValue(exercise, index, 'time') || '0'}
                              style={{ borderRadius: 'var(--ios-radius-sm)' }}
                            />
                          </div>
                        )}
                        {hasField(exercise, index, 'distance') && (
                          <div className="col-4 sm:col-4 md:col-3">
                            <label style={styles.inputLabel}>
                              {intl.formatMessage(
                                { id: 'training.exercise.distance' },
                                { unit: propertyUnits?.distance || 'km' }
                              )}
                            </label>
                            <InputText
                              value={setData.distance || ''}
                              onChange={(e) => handleExerciseChange(exercise.id, index, 'distance', e.target.value)}
                              className="p-inputtext-sm w-full text-center"
                              placeholder={getSetTemplateValue(exercise, index, 'distance') || '0'}
                              style={{ borderRadius: 'var(--ios-radius-sm)' }}
                            />
                          </div>
                        )}
                        {hasField(exercise, index, 'duration') && (
                          <div className="col-4 sm:col-4 md:col-3">
                            <label style={styles.inputLabel}>
                              {intl.formatMessage(
                                { id: 'training.exercise.duration' },
                                { unit: propertyUnits?.duration || 's' }
                              )}
                            </label>
                            <InputText
                              value={setData.duration || ''}
                              onChange={(e) => handleExerciseChange(exercise.id, index, 'duration', e.target.value)}
                              className="p-inputtext-sm w-full text-center"
                              placeholder={getSetTemplateValue(exercise, index, 'duration') || '0'}
                              style={{ borderRadius: 'var(--ios-radius-sm)' }}
                            />
                          </div>
                        )}
                        {hasField(exercise, index, 'difficulty') && (
                          <div className="col-4 sm:col-4 md:col-3">
                            <label style={styles.inputLabel}>
                              {intl.formatMessage(
                                { id: 'training.exercise.difficulty' },
                                { unit: propertyUnits?.difficulty || '' }
                              )}
                            </label>
                            <InputText
                              value={setData.difficulty || ''}
                              onChange={(e) => handleExerciseChange(exercise.id, index, 'difficulty', e.target.value)}
                              className="p-inputtext-sm w-full text-center"
                              placeholder={getSetTemplateValue(exercise, index, 'difficulty') || '0'}
                              style={{ borderRadius: 'var(--ios-radius-sm)' }}
                            />
                          </div>
                        )}
                        {hasField(exercise, index, 'tempo') && (
                          <div className="col-4 sm:col-4 md:col-3">
                            <label style={styles.inputLabel}>
                              {intl.formatMessage(
                                { id: 'training.exercise.tempo' },
                                { unit: propertyUnits?.tempo || '' }
                              )}
                            </label>
                            <InputText
                              value={setData.tempo || ''}
                              onChange={(e) => handleExerciseChange(exercise.id, index, 'tempo', e.target.value)}
                              className="p-inputtext-sm w-full text-center"
                              placeholder={getSetTemplateValue(exercise, index, 'tempo') || '0'}
                              style={{ borderRadius: 'var(--ios-radius-sm)' }}
                            />
                          </div>
                        )}
                        {(() => {
                          const resolvedRpe = getExerciseRpeMethod(exercise);
                          if (!resolvedRpe) return null;
                          return (
                            <div className="col-12">
                              <QuickCompleteRpe
                                rpeMethod={resolvedRpe}
                                selectedRaw={setData.rating ?? null}
                                onSelect={(value) => {
                                  handleExerciseChange(exercise.id, index, 'rating', value);
                                  handleSetCompletedChange(exercise.id, index, true);
                                }}
                                onSkip={() => {
                                  handleExerciseChange(exercise.id, index, 'rating', null);
                                  handleSetCompletedChange(exercise.id, index, false);
                                }}
                                fallbackPicker={(onChange) => (
                                  <RpeDropdownComponent
                                    selectedRpe={setData.rating ?? 0}
                                    onChange={onChange}
                                    rpeMethod={resolvedRpe}
                                  />
                                )}
                              />
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Notes & Comments */}
              <div className="grid mt-2">
                {exercise.notes && (
                  <div className="col-12 sm:col-6">
                    <label
                      htmlFor={`notes-${exercise.id}`}
                      style={{ ...styles.inputLabel, textTransform: 'none', fontSize: '0.78rem' }}
                    >
                      <FormattedMessage id="training.notes" defaultMessage="Notes" />
                    </label>
                    <InputTextarea
                      id={`notes-${exercise.id}`}
                      rows={1}
                      value={exercise.notes || ''}
                      disabled
                      className="w-full text-sm"
                      style={{
                        resize: 'none',
                        borderRadius: 'var(--ios-radius-md)',
                        background: 'var(--ios-surface-subtle)',
                        border: '1px solid var(--ios-divider)',
                        opacity: 0.8
                      }}
                    />
                  </div>
                )}
                <div className={exercise.notes ? 'col-12 sm:col-6' : 'col-12'}>
                  <label
                    htmlFor={`comments-${exercise.id}`}
                    style={{ ...styles.inputLabel, textTransform: 'none', fontSize: '0.78rem' }}
                  >
                    <FormattedMessage id="training.comments" defaultMessage="Comments" />
                  </label>
                  <InputTextarea
                    id={`comments-${exercise.id}`}
                    rows={1}
                    value={progress.comments || ''}
                    onChange={(e) => handleExerciseChange(exercise.id, null, 'comments', e.target.value)}
                    className="w-full text-sm"
                    placeholder={intl.formatMessage({
                      id: 'training.comments.placeholder',
                      defaultMessage: 'Add your comments here...'
                    })}
                    style={{
                      resize: 'none',
                      borderRadius: 'var(--ios-radius-md)',
                      border: '1px solid var(--ios-divider)'
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function TrainingPlanDetails({ setPlanDetailsVisible, setRefreshKey }: ITrainingPlanDetailsProps) {
  const hook = useTrainingPlanDetails({ setPlanDetailsVisible, setRefreshKey });

  const {
    plan,
    exerciseProgress,
    videoDialogVisible,
    currentVideoUrl,
    finishDialogVisible,
    confirmFinishVisible,
    confirmExitVisible,
    currentGroupIndex,
    sessionTimer,
    isTimerPaused,
    propertyUnits,
    loading,
    hasSubjectiveMeasurement,
    setVideoDialogVisible,
    setFinishDialogVisible,
    setConfirmFinishVisible,
    setConfirmExitVisible,
    resumeAfterCancelFinish,
    handleExitTraining,
    handleSaveProgress,
    handleClearProgress,
    handleSubmitFeedback,
    handleToggleTimer,
    handleToggleAll,
    handleVideoClick,
    handleSetCompletedChange,
    handleExerciseChange,
    handleFinishClick,
    markAllAsCompleted,
    fillUnmarkedAsNotCompleted,
    navigateToNextGroup,
    navigateToPreviousGroup,
    navigateToGroup,
    canNavigateToNextGroup,
    getStatusIcon,
    isExerciseCompleted,
    isGroupCompleted,
    getExerciseRpeMethod,
    formatSessionTime,
    intl
  } = hook;

  // Single batched fetch for the "Última: 8×60kg @ RPE 7" compare chips.
  // Before this we did one GET per exercise on mount (N+1). One POST now
  // returns the whole map. See qa-audit/2026-05-21 Bug 10.
  const [lastSetsMap, setLastSetsMap] = useState<LastSetsMap>({});
  const exerciseIds = useMemo<number[]>(() => {
    if (!plan?.groups) return [];
    const ids = new Set<number>();
    for (const g of plan.groups as IExerciseGroup[]) {
      for (const ex of g.exercises || []) {
        if (ex.exercise?.id) ids.add(ex.exercise.id);
      }
    }
    return Array.from(ids);
  }, [plan?.groups]);

  useEffect(() => {
    if (exerciseIds.length === 0) {
      setLastSetsMap({});
      return;
    }
    let cancelled = false;
    api.workout
      .fetchLastSetsBatch(exerciseIds)
      .then((res) => {
        if (cancelled) return;
        const body = (res?.data ?? res) as LastSetsMap;
        setLastSetsMap(body || {});
      })
      .catch(() => {
        if (!cancelled) setLastSetsMap({});
      });
    return () => {
      cancelled = true;
    };
  }, [exerciseIds]);

  // Live coach feed — notify on session open + heartbeat every 30s while the
  // tab is visible. The heartbeat keeps the backend's in-memory snapshot
  // warm so a coach who refreshes their dashboard mid-session still sees
  // this atleta. Errors are swallowed: the feed is a nice-to-have.
  useEffect(() => {
    if (!plan?.id) return;
    const planId = Number(plan.id);
    const planName = plan.instanceName || plan.workout?.planName;
    const totalExercises = plan.groups?.reduce((acc: number, g: IExerciseGroup) => acc + (g.exercises?.length ?? 0), 0);

    const fire = () =>
      api.workout.notifySessionActivity(planId, { type: 'session:opened', planName, totalExercises }).catch(() => {
        /* live-feed is best-effort */
      });

    fire();
    const interval = setInterval(() => {
      if (document.hidden) return;
      fire();
    }, 30 * 1000);
    return () => clearInterval(interval);
  }, [plan?.id, plan?.instanceName, plan?.workout?.planName, plan?.groups]);

  if (loading || !plan) {
    return (
      <div className="flex flex-column align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
        <ProgressSpinner style={{ width: '2.5rem', height: '2.5rem' }} />
        <span style={{ color: 'var(--ios-text-secondary)', fontSize: '0.9rem', marginTop: '0.75rem' }}>
          <FormattedMessage id="training.loading" />
        </span>
      </div>
    );
  }

  return (
    <LastSetsContext.Provider value={lastSetsMap}>
      <div style={styles.page}>
        {/* Header Card */}
        <div style={styles.headerCard}>
          <h1 style={styles.planTitle}>
            <FormattedMessage id="training.title" />
          </h1>
          <p style={styles.planSubtitle}>{plan.instanceName || plan.workout.planName}</p>
          {!plan.isTemplate && (
            <div style={styles.statusBadge}>
              <i className={getStatusIcon(plan.status)} style={{ fontSize: '0.8rem' }} />
              <span>
                <FormattedMessage id="training.status" />: {plan.status}
              </span>
            </div>
          )}
        </div>

        {/* Group Navigation + Content */}
        <RenderExerciseGroup
          group={plan.groups[currentGroupIndex]!}
          exerciseProgress={exerciseProgress}
          isGroupCompleted={isGroupCompleted}
          isExerciseCompleted={isExerciseCompleted}
          handleToggleAll={handleToggleAll}
          handleVideoClick={handleVideoClick}
          handleSetCompletedChange={handleSetCompletedChange}
          handleExerciseChange={handleExerciseChange}
          propertyUnits={propertyUnits}
          getExerciseRpeMethod={getExerciseRpeMethod}
          intl={intl}
        />

        {/* Group Indicators */}
        <div className="flex justify-content-center align-items-center gap-2 mt-3 mb-3">
          {plan.groups.map((_: IExerciseGroup, index: number) => (
            <div
              key={index}
              onClick={() => navigateToGroup(index)}
              style={styles.groupDot(index === currentGroupIndex, isGroupCompleted(plan.groups[index]!))}
            />
          ))}
        </div>

        {/* Sticky Action Bar */}
        <div style={styles.stickyBar}>
          {/* Chevron nav buttons */}
          <Button
            icon="pi pi-chevron-left"
            className="p-button-rounded p-button-text"
            onClick={navigateToPreviousGroup}
            disabled={currentGroupIndex === 0}
            style={{
              width: '2.2rem',
              height: '2.2rem',
              color: currentGroupIndex === 0 ? 'var(--ios-text-tertiary)' : '#4d7c0f',
              flexShrink: 0
            }}
          />
          <Button
            icon="pi pi-chevron-right"
            className="p-button-rounded p-button-text"
            onClick={navigateToNextGroup}
            disabled={!canNavigateToNextGroup()}
            style={{
              width: '2.2rem',
              height: '2.2rem',
              color: !canNavigateToNextGroup() ? 'var(--ios-text-tertiary)' : '#4d7c0f',
              flexShrink: 0
            }}
          />
          {/* Timer Pill */}
          <div style={styles.timerPill}>
            <i className="pi pi-stopwatch" style={{ fontSize: '0.75rem' }} />
            <span style={{ fontWeight: 700, fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}>
              {formatSessionTime(sessionTimer)}
            </span>
            <Button
              icon={isTimerPaused ? 'pi pi-play' : 'pi pi-pause'}
              className="p-button-text p-button-sm"
              onClick={handleToggleTimer}
              style={{
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                width: '1.4rem',
                height: '1.4rem',
                borderRadius: '6px',
                padding: 0
              }}
            />
          </div>

          <Button
            icon="pi pi-save"
            className="p-button-rounded"
            onClick={handleSaveProgress}
            tooltip={intl.formatMessage({ id: 'training.buttons.saveProgress' })}
            tooltipOptions={{ position: 'top' }}
            style={styles.actionBtn('#4d7c0f')}
          />
          <Button
            icon="pi pi-trash"
            className="p-button-rounded p-button-outlined"
            onClick={handleClearProgress}
            tooltip={intl.formatMessage({ id: 'training.buttons.clearProgress', defaultMessage: 'Clear Progress' })}
            tooltipOptions={{ position: 'top' }}
            style={styles.actionBtn('transparent', '#ef4444')}
          />
          {/* (#32) Volver sin finalizar: avisa qué pasa con lo que marcó.
              Flecha y no `pi-sign-out`: ese ícono ya es el de cerrar sesión en el
              Sidebar y dos salidas distintas con el mismo dibujo se confunden. */}
          <Button
            icon="pi pi-arrow-left"
            className="p-button-rounded p-button-outlined"
            onClick={() => setConfirmExitVisible(true)}
            tooltip={intl.formatMessage({ id: 'training.buttons.exit' })}
            tooltipOptions={{ position: 'top' }}
            style={styles.actionBtn('transparent', 'var(--ios-text-secondary)')}
          />
          <Button
            icon="pi pi-flag-fill"
            className="p-button-rounded"
            onClick={handleFinishClick}
            tooltip={intl.formatMessage({ id: 'training.buttons.finishTraining' })}
            tooltipOptions={{ position: 'top' }}
            style={styles.actionBtn('#22c55e')}
          />
        </div>

        {/* (#32) Confirmation Dialog: salir sin finalizar */}
        <Dialog
          visible={confirmExitVisible}
          onHide={() => setConfirmExitVisible(false)}
          draggable={false}
          resizable={false}
          dismissableMask
          style={{ width: '26rem', maxWidth: '92vw' }}
          header={
            <div className="flex align-items-center gap-2">
              <i className="pi pi-arrow-left" style={{ color: '#f59e0b', fontSize: '1.1rem' }} />
              <span style={{ fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.01em' }}>
                <FormattedMessage id="training.exit.title" defaultMessage="Leave the training?" />
              </span>
            </div>
          }
        >
          <div className="flex flex-column gap-3">
            <p className="m-0" style={{ color: 'var(--ios-text-secondary)', fontSize: '0.9rem' }}>
              <FormattedMessage id="training.exit.body" />
            </p>
            <div className="flex flex-column gap-2">
              <Button
                label={intl.formatMessage({ id: 'training.exit.keepTraining' })}
                icon="pi pi-play"
                onClick={() => setConfirmExitVisible(false)}
                style={{
                  background: '#4d7c0f',
                  border: 'none',
                  borderRadius: 'var(--ios-radius-md)',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  width: '100%'
                }}
              />
              <Button
                label={intl.formatMessage({ id: 'training.exit.confirm' })}
                icon="pi pi-arrow-left"
                className="p-button-text"
                onClick={handleExitTraining}
                style={{ fontSize: '0.85rem', color: 'var(--ios-text-secondary)', width: '100%' }}
              />
            </div>
          </div>
        </Dialog>

        {/* Confirmation Dialog: "Did you finish everything?" */}
        <Dialog
          visible={confirmFinishVisible}
          onHide={() => {
            setConfirmFinishVisible(false);
            resumeAfterCancelFinish();
          }}
          header={
            <div className="flex align-items-center gap-2">
              <i className="pi pi-question-circle" style={{ color: '#f59e0b', fontSize: '1.1rem' }} />
              <span style={{ fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.01em' }}>
                <FormattedMessage id="training.confirm.title" defaultMessage="Did you finish everything?" />
              </span>
            </div>
          }
          style={{ width: 'min(92vw, 420px)', borderRadius: 'var(--ios-radius-xl)' }}
          draggable={false}
          resizable={false}
          dismissableMask
          footer={
            <div className="flex flex-column gap-2" style={{ padding: '0.25rem 0' }}>
              <Button
                label={intl.formatMessage({
                  id: 'training.confirm.savePartial',
                  defaultMessage: 'Save what I did'
                })}
                icon="pi pi-save"
                onClick={() => {
                  fillUnmarkedAsNotCompleted();
                  setConfirmFinishVisible(false);
                  setFinishDialogVisible(true);
                }}
                style={{
                  background: '#a3e635',
                  border: 'none',
                  borderRadius: 'var(--ios-radius-md)',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  width: '100%'
                }}
              />
              <Button
                label={intl.formatMessage({
                  id: 'training.confirm.markAllDone',
                  defaultMessage: 'Mark all as finished'
                })}
                icon="pi pi-check-circle"
                onClick={() => {
                  markAllAsCompleted();
                  setConfirmFinishVisible(false);
                  if (!hasSubjectiveMeasurement) {
                    setFinishDialogVisible(true);
                  }
                }}
                style={{
                  background: '#22c55e',
                  border: 'none',
                  borderRadius: 'var(--ios-radius-md)',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  width: '100%'
                }}
              />
              <Button
                label={intl.formatMessage({
                  id: 'training.confirm.goBack',
                  defaultMessage: 'Go back and mark what you missed'
                })}
                icon="pi pi-arrow-left"
                className="p-button-text"
                onClick={() => {
                  setConfirmFinishVisible(false);
                  resumeAfterCancelFinish();
                }}
                style={{
                  fontSize: '0.85rem',
                  color: 'var(--ios-text-secondary)',
                  width: '100%'
                }}
              />
            </div>
          }
        >
          <p style={{ fontSize: '0.9rem', color: 'var(--ios-text)', lineHeight: 1.5, margin: '0.5rem 0' }}>
            <FormattedMessage
              id="training.confirm.messagePartial"
              defaultMessage="You haven't marked all sets. You can save what you did (unmarked sets will be saved as not completed) or mark everything as finished."
            />
          </p>
        </Dialog>

        <FinishTrainingDialog
          visible={finishDialogVisible}
          onHide={() => {
            setFinishDialogVisible(false);
            resumeAfterCancelFinish();
          }}
          submitFeedback={handleSubmitFeedback}
          sessionTimer={sessionTimer}
        />

        <VideoDialog
          visible={videoDialogVisible}
          onHide={() => setVideoDialogVisible(false)}
          videoUrl={currentVideoUrl}
        />
      </div>
    </LastSetsContext.Provider>
  );
}
